import type { CockpitTeamFundraising, CockpitFundraisingWindowBounds } from '../../types';
import { formatDollarsCompact } from '../../lib/snapshotMetrics';
import { YoyPill } from './YoyPill';
import { FundraiserTrendBars } from './FundraiserTrendBars';
import { pickWindow, seasonLabel, windowCaption, type FundMetric } from '../../lib/fundraisingWindow';
import { SegmentPills } from '../shared/SegmentPills';

export function TeamFundraisingYoY({ data, metric, onMetricChange, bounds }: {
  data: CockpitTeamFundraising; metric: FundMetric; onMetricChange: (m: FundMetric) => void;
  bounds?: CockpitFundraisingWindowBounds;
}) {
  const w = pickWindow(data.windows, metric, { season: data.season });
  const caption = windowCaption(metric, bounds, data.season);
  const avgPerCampaign = w.campaigns === 0 ? '0' : (BigInt(w.raisedCents) / BigInt(w.campaigns)).toString();
  const empty = w.raisedCents === '0' && data.trend.every((t) => t.raisedCents === '0');

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Team Fundraising — Year over Year</h3>
        <SegmentPills
          activeKey={metric}
          onSelect={(k) => onMetricChange(k as FundMetric)}
          pills={[
            { key: 'calYtd', label: 'YTD', count: null },
            { key: 'season', label: data.season ? seasonLabel(data.season.currentSeasonYear) : 'Season', count: null },
          ]}
        />
      </div>

      {/* CRM-121: say what window this is. Nothing on this page used to. */}
      {caption && (
        <div className="px-4 pt-2 pb-1 text-[11px] text-gray-500 dark:text-gray-400">{caption}</div>
      )}

      {empty ? (
        <div className="p-6 text-sm text-gray-400 dark:text-gray-500 text-center">No fundraiser data yet.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-100 dark:divide-gray-700/60">
            <div className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold">Raised {w.label}</div>
              <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">{formatDollarsCompact(w.raisedCents)}</div>
              <div className="mt-1 text-xs"><YoyPill pct={w.yoyPct} /> <span className="text-gray-400 dark:text-gray-500">vs {formatDollarsCompact(w.priorCents)}</span></div>
            </div>
            <div className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold">Fundraisers</div>
              <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">{w.campaigns}</div>
            </div>
            <div className="p-4">
              {/* CRM-121: this tile does NOT follow the toggle — it is always the
                  school year. Say so rather than let it look like the others. */}
              <div
                className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold"
                title="Always the school year to date — this tile does not follow the YTD/season toggle."
              >
                Avg participation <span className="normal-case font-normal">(school yr)</span>
              </div>
              <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">{data.avgParticipationThisSy === null ? '—' : `${Math.round(data.avgParticipationThisSy)}%`}</div>
            </div>
            <div className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold">Avg per fundraiser</div>
              <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">{w.campaigns === 0 ? '—' : formatDollarsCompact(avgPerCampaign)}</div>
            </div>
          </div>
          <FundraiserTrendBars trend={data.trend} />
        </>
      )}
    </div>
  );
}
